import React, { useEffect, useState } from "react";
import {
  Search,
  Download,
  Filter,
  Eye,
  Trash2,
  Package,
  X,
  Save,
  Plus,
  RefreshCcw,
  MessageCircle,
} from "lucide-react";
import { adminOrders } from "../../services/adminApi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { toast } from "sonner";

const STATUSES = [
  { value: "nueva", label: "Nueva" },
  { value: "preparando", label: "Preparando" },
  { value: "enviado", label: "Enviado" },
  { value: "entregado", label: "Entregado" },
  { value: "cancelado", label: "Cancelado" },
];

const STATUS_COLORS = {
  nueva: "bg-blue-100 text-blue-800 border-blue-200",
  preparando: "bg-amber-100 text-amber-800 border-amber-200",
  enviado: "bg-purple-100 text-purple-800 border-purple-200",
  entregado: "bg-green-100 text-green-800 border-green-200",
  cancelado: "bg-red-100 text-red-800 border-red-200",
};

const PAYMENT_COLORS = {
  pending: "bg-amber-50 text-amber-700",
  completed: "bg-green-50 text-green-700",
  approved: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-700",
  refunded: "bg-stone-100 text-stone-700",
};

const fmt = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  return date.toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSyncMP = async () => {
    if (
      !window.confirm(
        "Se buscarán en tu cuenta de MercadoPago todos los pagos de los últimos 90 días y se importarán los que falten. ¿Continuar?"
      )
    )
      return;
    setSyncing(true);
    try {
      const res = await adminOrders.syncMercadoPago(90);
      toast.success(
        `Sincronización lista: ${res.created.length} pedido${
          res.created.length === 1 ? "" : "s"
        } recuperado${res.created.length === 1 ? "" : "s"}`,
        {
          description: `Escaneados: ${res.total_scanned} · Omitidos: ${res.skipped.length}`,
        }
      );
      load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Error al sincronizar con MercadoPago"
      );
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const data = await adminOrders.list(params);
      setOrders(data.orders);
      setTotal(data.total);
    } catch (err) {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter]);

  const filteredOrders = orders.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.customer_info?.name?.toLowerCase().includes(q) ||
      o.customer_info?.email?.toLowerCase().includes(q) ||
      o.id?.toLowerCase().includes(q) ||
      o.tracking_number?.toLowerCase().includes(q)
    );
  });

  const handleExport = async () => {
    try {
      const res = await adminOrders.exportCsv();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedidos_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este pedido? Esta acción no se puede deshacer."))
      return;
    try {
      await adminOrders.remove(id);
      toast.success("Pedido eliminado");
      setSelected(null);
      load();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-orders-page">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Pedidos</h1>
          <p className="text-stone-500 mt-1">
            {total} pedido{total === 1 ? "" : "s"} en total
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleSyncMP}
            disabled={syncing}
            variant="outline"
            className="border-green-200 text-green-800 hover:bg-green-50"
            data-testid="admin-sync-mp"
          >
            <RefreshCcw
              className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`}
            />
            {syncing ? "Sincronizando..." : "Sincronizar con MP"}
          </Button>
          <Button
            onClick={() => setShowManualForm(true)}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="admin-new-manual-order"
          >
            <Plus className="w-4 h-4 mr-2" />
            Registrar pedido
          </Button>
          <Button
            onClick={handleExport}
            variant="outline"
            className="border-amber-200 text-amber-800 hover:bg-amber-50"
            data-testid="admin-export-csv"
          >
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Buscar por nombre, email, ID o guía..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="admin-orders-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="admin-status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrar estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone-400">Cargando...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500">No hay pedidos que mostrar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left text-xs font-semibold text-stone-600 uppercase px-4 py-3">
                    Fecha
                  </th>
                  <th className="text-left text-xs font-semibold text-stone-600 uppercase px-4 py-3">
                    Cliente
                  </th>
                  <th className="text-left text-xs font-semibold text-stone-600 uppercase px-4 py-3">
                    Total
                  </th>
                  <th className="text-left text-xs font-semibold text-stone-600 uppercase px-4 py-3">
                    Pago
                  </th>
                  <th className="text-left text-xs font-semibold text-stone-600 uppercase px-4 py-3">
                    Estado
                  </th>
                  <th className="text-right text-xs font-semibold text-stone-600 uppercase px-4 py-3">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <tr
                    key={o.id}
                    className="border-b border-stone-100 hover:bg-stone-50 transition-colors"
                    data-testid={`order-row-${o.id}`}
                  >
                    <td className="px-4 py-3 text-sm text-stone-600">
                      {fmtDate(o.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-stone-800 text-sm">
                          {o.customer_info?.name}
                        </p>
                        <p className="text-xs text-stone-500">
                          {o.customer_info?.email}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-stone-800">
                      {fmt(o.total)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                          PAYMENT_COLORS[o.payment_status] ||
                          "bg-stone-100 text-stone-700"
                        }`}
                      >
                        {o.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                          STATUS_COLORS[o.fulfillment_status || "nueva"]
                        }`}
                      >
                        {STATUSES.find(
                          (s) => s.value === (o.fulfillment_status || "nueva")
                        )?.label || "Nueva"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelected(o)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-amber-100 text-amber-700"
                        data-testid={`view-order-${o.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onUpdated={(o) => {
            setSelected(o);
            load();
          }}
          onDelete={handleDelete}
        />
      )}

      {/* Manual order modal */}
      {showManualForm && (
        <ManualOrderModal
          onClose={() => setShowManualForm(false)}
          onCreated={() => {
            setShowManualForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function OrderDetailModal({ order, onClose, onUpdated, onDelete }) {
  const [form, setForm] = useState({
    fulfillment_status: order.fulfillment_status || "nueva",
    tracking_number: order.tracking_number || "",
    carrier: order.carrier || "",
    admin_notes: order.admin_notes || "",
  });
  const [customer, setCustomer] = useState({
    name: order.customer_info?.name || "",
    email: order.customer_info?.email || "",
    phone: order.customer_info?.phone || "",
    address: order.customer_info?.address || "",
    city: order.customer_info?.city || "",
    state: order.customer_info?.state || "",
    zip_code: order.customer_info?.zip_code || "",
  });
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (editingCustomer) payload.customer_info = customer;
      const updated = await adminOrders.update(order.id, payload);
      toast.success("Pedido actualizado");
      setEditingCustomer(false);
      onUpdated(updated);
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const c = order.customer_info || {};

  // Build WhatsApp URL: prefill message asking for missing info
  const buildWhatsAppUrl = () => {
    const firstName = (c.name || "").split(" ")[0] || "";
    const orderShort = (order.id || "").slice(0, 8).toUpperCase();
    const greeting = firstName ? `¡Hola ${firstName}!` : "¡Hola!";
    const message = `${greeting} 🌸 Te escribo de Burbujas de mami 🍯. Recibí tu pedido *#${orderShort}* y quiero confirmarte los datos de envío para que llegue perfecto. ¿Me puedes compartir por favor:\n\n• Dirección completa (calle, número, colonia)\n• Ciudad y estado\n• Código Postal\n\n¡Muchas gracias! 💛`;
    const encoded = encodeURIComponent(message);

    // Clean phone: keep only digits
    const phoneDigits = (c.phone || "").replace(/\D/g, "");
    // If phone is 10 digits, prepend Mexico country code (52)
    let phone = "";
    if (phoneDigits.length === 10) phone = `52${phoneDigits}`;
    else if (phoneDigits.length === 12 && phoneDigits.startsWith("52")) phone = phoneDigits;
    else if (phoneDigits.length >= 11) phone = phoneDigits;

    return phone
      ? `https://wa.me/${phone}?text=${encoded}`
      : `https://wa.me/?text=${encoded}`;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[95vh] sm:rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="order-detail-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-stone-800">
              Pedido #{order.id?.slice(0, 8)}
            </h2>
            <p className="text-xs text-stone-500">{fmtDate(order.created_at)}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Customer */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider">
                Cliente
              </h3>
              <button
                onClick={() => setEditingCustomer(!editingCustomer)}
                className="text-xs text-amber-700 hover:text-amber-800 font-medium"
                data-testid="toggle-edit-customer"
              >
                {editingCustomer ? "Cancelar" : "✏️ Editar"}
              </button>
            </div>

            {!editingCustomer ? (
              <>
                <div className="bg-stone-50 rounded-xl p-4 space-y-1 text-sm">
                  <p className="font-semibold text-stone-800">
                    {c.name || <span className="text-stone-400 italic">Sin nombre</span>}
                  </p>
                  <p className="text-stone-600">
                    {c.email || <span className="text-stone-400 italic">Sin email</span>}
                  </p>
                  <p className="text-stone-600">
                    {c.phone || <span className="text-stone-400 italic">Sin teléfono</span>}
                  </p>
                  <div className="pt-2 border-t border-stone-200 mt-2 text-stone-700">
                    <p>
                      {c.address || <span className="text-stone-400 italic">Sin dirección</span>}
                    </p>
                    <p>
                      {[c.city, c.state, c.zip_code].filter(Boolean).join(", ") || (
                        <span className="text-stone-400 italic">Ciudad/Estado/CP</span>
                      )}
                    </p>
                  </div>
                </div>
                <a
                  href={buildWhatsAppUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-[#25D366] hover:bg-[#128C7E] text-white font-medium text-sm transition-colors"
                  data-testid="contact-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                  Contactar por WhatsApp
                </a>
                {!c.phone && (
                  <p className="text-xs text-stone-500 mt-1.5 text-center">
                    ⚠️ Sin teléfono guardado — se abrirá WhatsApp para que elijas el contacto.
                  </p>
                )}
              </>
            ) : (
              <div
                className="bg-amber-50/40 border border-amber-200 rounded-xl p-4 space-y-3 text-sm"
                data-testid="customer-edit-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Nombre completo</Label>
                    <Input
                      value={customer.name}
                      onChange={(e) =>
                        setCustomer({ ...customer, name: e.target.value })
                      }
                      className="mt-1"
                      data-testid="edit-customer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={customer.email}
                      onChange={(e) =>
                        setCustomer({ ...customer, email: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Teléfono</Label>
                    <Input
                      value={customer.phone}
                      onChange={(e) =>
                        setCustomer({ ...customer, phone: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Dirección (calle, número, colonia)</Label>
                    <Input
                      value={customer.address}
                      onChange={(e) =>
                        setCustomer({ ...customer, address: e.target.value })
                      }
                      className="mt-1"
                      placeholder="Av. Ejemplo 123, Col. Centro"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Ciudad</Label>
                    <Input
                      value={customer.city}
                      onChange={(e) =>
                        setCustomer({ ...customer, city: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Estado</Label>
                    <Input
                      value={customer.state}
                      onChange={(e) =>
                        setCustomer({ ...customer, state: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Código Postal</Label>
                    <Input
                      value={customer.zip_code}
                      onChange={(e) =>
                        setCustomer({ ...customer, zip_code: e.target.value })
                      }
                      className="mt-1"
                    />
                  </div>
                </div>
                <p className="text-xs text-amber-800">
                  💡 Los cambios se guardarán al dar click en &quot;Guardar cambios&quot; abajo.
                </p>
              </div>
            )}
          </section>

          {/* Items */}
          <section>
            <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">
              Productos
            </h3>
            <div className="border border-stone-200 rounded-xl overflow-hidden">
              {(order.items || []).map((item, i) => (
                <div
                  key={i}
                  className="flex justify-between p-3 border-b last:border-b-0 border-stone-100 text-sm"
                >
                  <div>
                    <p className="font-medium text-stone-800">
                      {item.product_name}
                    </p>
                    <p className="text-xs text-stone-500">
                      {item.variant_size} · x{item.quantity}
                    </p>
                  </div>
                  <p className="font-semibold text-stone-700">
                    {fmt(item.subtotal)}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal</span>
                <span>{fmt(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>Envío</span>
                <span>{fmt(order.shipping)}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-stone-800 pt-2 border-t border-stone-200">
                <span>Total</span>
                <span>{fmt(order.total)}</span>
              </div>
            </div>
          </section>

          {/* Payment */}
          <section>
            <h3 className="text-sm font-semibold text-stone-700 uppercase tracking-wider mb-3">
              Pago
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-stone-500">Método</p>
                <p className="font-medium text-stone-800 capitalize">
                  {order.payment_method}
                </p>
              </div>
              <div>
                <p className="text-xs text-stone-500">Estado</p>
                <span
                  className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                    PAYMENT_COLORS[order.payment_status] ||
                    "bg-stone-100 text-stone-700"
                  }`}
                >
                  {order.payment_status}
                </span>
              </div>
              {order.payment_id && (
                <div className="col-span-2">
                  <p className="text-xs text-stone-500">ID de pago</p>
                  <p className="font-mono text-xs text-stone-700 break-all">
                    {order.payment_id}
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Admin actions */}
          <section className="bg-amber-50/40 border border-amber-100 rounded-xl p-4 space-y-4">
            <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wider">
              Gestión del pedido
            </h3>

            <div>
              <Label className="text-xs">Estado del pedido</Label>
              <Select
                value={form.fulfillment_status}
                onValueChange={(v) =>
                  setForm({ ...form, fulfillment_status: v })
                }
              >
                <SelectTrigger className="mt-1" data-testid="modal-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Paquetería</Label>
                <Input
                  value={form.carrier}
                  onChange={(e) =>
                    setForm({ ...form, carrier: e.target.value })
                  }
                  placeholder="Estafeta, DHL, etc."
                  className="mt-1"
                  data-testid="modal-carrier"
                />
              </div>
              <div>
                <Label className="text-xs">Número de guía</Label>
                <Input
                  value={form.tracking_number}
                  onChange={(e) =>
                    setForm({ ...form, tracking_number: e.target.value })
                  }
                  placeholder="123456789"
                  className="mt-1"
                  data-testid="modal-tracking"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Notas internas</Label>
              <Textarea
                value={form.admin_notes}
                onChange={(e) =>
                  setForm({ ...form, admin_notes: e.target.value })
                }
                placeholder="Notas privadas para ti..."
                rows={3}
                className="mt-1"
                data-testid="modal-notes"
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-200 flex items-center justify-between gap-3 bg-stone-50">
          <button
            onClick={() => onDelete(order.id)}
            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
            data-testid="modal-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            Eliminar
          </button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cerrar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-700 hover:bg-amber-800"
              data-testid="modal-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ManualOrderModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    payment_id: "",
    payment_status: "completed",
    admin_notes: "",
  });
  const [items, setItems] = useState([
    { product_name: "", variant_size: "", variant_price: 0, quantity: 1 },
  ]);
  const [saving, setSaving] = useState(false);

  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    setItems(next);
  };

  const subtotal = items.reduce(
    (s, i) => s + (parseFloat(i.variant_price) || 0) * (parseInt(i.quantity) || 0),
    0
  );
  const total = subtotal + 200;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminOrders.createManual({
        customer_info: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
        },
        items: items
          .filter((i) => i.product_name?.trim())
          .map((i) => ({
            product_name: i.product_name,
            variant_size: i.variant_size,
            variant_price: parseFloat(i.variant_price) || 0,
            quantity: parseInt(i.quantity) || 1,
          })),
        payment_id: form.payment_id || null,
        payment_status: form.payment_status,
        admin_notes: form.admin_notes || null,
      });
      toast.success("Pedido registrado");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar pedido");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-2xl max-h-[95vh] sm:rounded-2xl overflow-hidden flex flex-col"
        data-testid="manual-order-modal"
      >
        <div className="px-6 py-4 border-b border-stone-200 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-bold text-stone-800">Registrar pedido</h2>
            <p className="text-xs text-stone-500">
              Útil para pagos de MP que no se registraron automáticamente
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <section>
            <h3 className="text-sm font-semibold text-stone-700 uppercase mb-3">
              Cliente
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Nombre completo *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Teléfono *</Label>
                <Input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Dirección *</Label>
                <Input
                  required
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1"
                  placeholder="Calle, número, colonia"
                />
              </div>
              <div>
                <Label>Ciudad *</Label>
                <Input
                  required
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Estado *</Label>
                <Input
                  required
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>CP *</Label>
                <Input
                  required
                  value={form.zip_code}
                  onChange={(e) =>
                    setForm({ ...form, zip_code: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-stone-700 uppercase">
                Productos
              </h3>
              <button
                type="button"
                onClick={() =>
                  setItems([
                    ...items,
                    { product_name: "", variant_size: "", variant_price: 0, quantity: 1 },
                  ])
                }
                className="text-sm text-amber-700 hover:text-amber-800 flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Agregar
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input
                    placeholder="Producto"
                    value={it.product_name}
                    onChange={(e) => updateItem(i, "product_name", e.target.value)}
                    className="col-span-5"
                    required
                  />
                  <Input
                    placeholder="Tamaño"
                    value={it.variant_size}
                    onChange={(e) => updateItem(i, "variant_size", e.target.value)}
                    className="col-span-3"
                  />
                  <Input
                    type="number"
                    placeholder="Precio"
                    value={it.variant_price}
                    onChange={(e) =>
                      updateItem(i, "variant_price", e.target.value)
                    }
                    className="col-span-2"
                    required
                  />
                  <Input
                    type="number"
                    min="1"
                    placeholder="Cant"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    className="col-span-1"
                    required
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setItems(items.filter((_, idx) => idx !== i))
                    }
                    className="col-span-1 p-2 text-red-500 hover:bg-red-50 rounded-lg justify-self-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-3 text-right text-sm">
              <p className="text-stone-500">
                Subtotal: <span className="font-semibold text-stone-800">${subtotal.toFixed(2)}</span>
                {" · "}Envío: <span className="font-semibold text-stone-800">$200.00</span>
              </p>
              <p className="text-base font-bold text-stone-800">
                Total: ${total.toFixed(2)} MXN
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-stone-700 uppercase mb-3">
              Pago
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>ID de pago MP</Label>
                <Input
                  value={form.payment_id}
                  onChange={(e) =>
                    setForm({ ...form, payment_id: e.target.value })
                  }
                  className="mt-1"
                  placeholder="opcional"
                />
              </div>
              <div>
                <Label>Estado del pago</Label>
                <Select
                  value={form.payment_status}
                  onValueChange={(v) =>
                    setForm({ ...form, payment_status: v })
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Pagado</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="approved">Aprobado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3">
              <Label>Notas</Label>
              <Textarea
                value={form.admin_notes}
                onChange={(e) =>
                  setForm({ ...form, admin_notes: e.target.value })
                }
                rows={2}
                className="mt-1"
                placeholder="Notas internas"
              />
            </div>
          </section>
        </div>

        <div className="px-6 py-4 border-t border-stone-200 flex justify-end gap-2 bg-stone-50">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-amber-700 hover:bg-amber-800"
            data-testid="manual-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Registrar pedido"}
          </Button>
        </div>
      </form>
    </div>
  );
}
